import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useRef } from 'react';
import { useHistory, useParams } from 'react-router';

import { DataAPIContext } from '../api';
import { GET_ONE } from '../api/dataProvider';
import useBatchDelete from '../bg_tasks/useBatchDelete';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import { imageURL } from '../components/images';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import RecordListPro from '../components/RecordListPro';
import ResizableSidebar from '../components/ResizableSidebar';
import SelectAllBanner from '../components/SelectAllBanner';
import useSelectAll from '../components/useSelectAll';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { ContextStore } from '../store/contextStore';
import ActivityCategoryEdit from './ActivityCategoryEdit';
import ActivityCategoryListToolbar from './ActivityCategoryListToolbar';
import { ConfirmationDialogText } from './utils';

function useIsWidthUp(breakpoint) {
    const theme = useTheme();
    return useMediaQuery(theme.breakpoints.up(breakpoint));
}

const useStyles = makeStyles((theme) => ({
    root: {
        paddingTop: 0,
        margin: theme.spacing(2),
        marginTop: '0.5rem',
        height: 'calc(100svh - 7rem)',
        display: 'flex',
        [theme.breakpoints.down('sm')]: {
            height: 'calc(100svh - 5rem)',
        },
    },
    recordList: {
        marginTop: theme.spacing(2),
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
    listItem: {
        height: '100%',
        maxHeight: '300px',
    },
}));

const ActivityCategoryList = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const urlParams = useParams();
    const history = useHistory();
    const classes = useStyles();
    const resource = 'activity_category';
    const [query, setQuery] = React.useState(null);
    const recordListRef = useRef();
    const [syncActive, setSyncActive] = React.useState(false);
    const [activeCategory, setActiveCategory] = React.useState(null);
    const [detailsDirty, setDetailsDirty] = React.useState(false);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const isSmallScreen = !useIsWidthUp('sm');
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const { hasPerm, settings, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);

    // "Select all matching" (CP-7771). RecordListPro owns the column filters, so select_all_ids gets the
    // filters the grid actually uses (onEffectiveFiltersChange), not just the static ones.
    const apiFilters = React.useMemo(() => [], []);
    const [gridFilters, setGridFilters] = React.useState(apiFilters);
    const {
        selectedIds, setSelectedIds, selectAll, deselectAll, selectAllMeta, loading: selectAllLoading,
    } = useSelectAll({ resource, apiFilters: gridFilters, query });

    const onRowSelectionModelChange = (selection) => {
        setSelectedIds(selection);
        if (!hasPerm('activity', 'read')) return;
        if (selection.length === 1) {
            history.push(`/activity-categories/${selection[0]}`);
        } else {
            history.push('/activity-categories');
            setActiveCategory(null);
        }
    };

    // CP-7771: bulk delete runs as a background task; the grid refreshes once it has finished.
    const refreshList = React.useCallback(() => setLastUpdate(new Date().toString()), []);
    const { deleteRecords } = useBatchDelete({ resource, names: ['activity category', 'activity categories'], onDeleted: refreshList });
    const doDeleteItemList = (idlist) => {
        setSyncActive(true);
        deleteRecords(idlist)
            .then(() => {
                setIdsToDelete(null);
                setActiveCategory(null);
            })
            .finally(() => {
                setSelectedIds([]);
                setSyncActive(false);
            });
    };

    const onDeleteItemList = (idlist) => {
        setIdsToDelete(idlist);
        setConfirmationDialogAction(idlist.length > 1 ? 'delete_activity_categories' : 'delete_activity_category');
    };

    const onItemDelete = (f) => {
        return doDeleteItemList([f.id]);
    };

    const onRowClick = (p) => {
        if (!hasPerm('activity', 'read')) return;
        if (!detailsDirty && isSmallScreen) {
            setDetailsDirty(false);
            history.push(`/activity-categories/${p.row.id}`);
        }
    };

    const onDetailsClose = () => {
        setActiveCategory(null);
        setDetailsDirty(false);
        setSelectedIds([]);
        history.push('/activity-categories');
    };

    const onCategorySave = () => {
        setLastUpdate(new Date().toString());
        setDetailsDirty(false);
    };

    React.useEffect(() => {
        if (!urlParams?.id) return;
        dataAPI(GET_ONE, resource, { id: urlParams.id }).then((res) => {
            setActiveCategory(res.data);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlParams.id]);

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        switch (confirmationDialogAction) {
            case 'delete_activity_category':
            case 'delete_activity_categories':
                doDeleteItemList(idsToDelete);
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const columns = [
        ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
        {
            field: 'icon', headerName: '', sortable: false, filterable: false, width: 50,
            renderCell: (p) => {
                const url = p.row.icon ? imageURL(settings.images.base, p.row.icon, 'cover_32x32') : null;
                return url ? <img src={url} alt="" width={32} height={32} style={{ borderRadius: 4 }} /> : null;
            },
        },
        { field: 'title', headerName: 'Title', sortable: false, flex: 1 },
        { field: 'atag', headerName: 'Access Tags', sortable: false, filterable: false, flex: 1, valueGetter: (p) => p.row.atag?.map((x) => x.name).join(', ') },
    ];

    const editPane = activeCategory ? (
        <ActivityCategoryEdit
            resource={resource}
            data={activeCategory}
            onClose={onDetailsClose}
            onChange={onCategorySave}
            onDelete={onItemDelete}
            syncActive={syncActive}
            setSyncActive={setSyncActive}
        />
    ) : null;

    const recordListProps = {
        ref: recordListRef,
        className: classes.recordList,
        resource: resource,
        checkboxSelection: hasPerm('activity', 'read'),
        rowSelectionModel: selectedIds,
        onRowSelectionModelChange: onRowSelectionModelChange,
        keepNonExistentRowsSelected: true,
        apiFilters: apiFilters,
        onEffectiveFiltersChange: setGridFilters,
        onRowClick: onRowClick,
        deps: lastUpdate,
        query: query,
        perPage: 100,
        columns: columns,
        disablePageUrlData: true,
    };

    const recordList = (
        <div style={{ position: 'relative' }}>
            <SelectAllBanner
                selectedIds={selectedIds}
                selectAllMeta={selectAllMeta}
                onSelectAll={selectAll}
                onDeselectAll={deselectAll}
                loading={selectAllLoading}
            />
            <RecordListPro {...recordListProps} />
        </div>
    );

    return (
        <div className={classes.root}>
            <PageMeta title="Activity Categories" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <FixedLinearProgress visibility={syncActive} />
            {isSmallScreen ? (
                <Grid container direction="row" spacing={2}>
                    {isSmallScreen && activeCategory ? null : (
                        <Grid item xs={12} style={{ height: "calc(100vh - 12.5rem)" }}>
                            <PageHeading sx={{ mt: 1, mb: 1 }}>Activity Categories</PageHeading>
                            <ActivityCategoryListToolbar
                                resource={resource}
                                selectedIds={selectedIds}
                                onQueryChange={setQuery}
                                onDeleteItem={onDeleteItemList}
                                isSmallScreen={isSmallScreen}
                            />
                            {recordList}
                        </Grid>
                    )}
                    {activeCategory ? (
                        <Grid item xs={12}>{editPane}</Grid>
                    ) : null}
                </Grid>
            ) : (
                <ResizableSidebar
                    storageKey="activity_categories.sidebar.width"
                    showRight={!!activeCategory}
                    left={
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <PageHeading sx={{ mt: 1, mb: 1 }}>Activity Categories</PageHeading>
                            <ActivityCategoryListToolbar
                                resource={resource}
                                selectedIds={selectedIds}
                                onQueryChange={setQuery}
                                onDeleteItem={onDeleteItemList}
                                isSmallScreen={isSmallScreen}
                            />
                            {recordList}
                        </div>
                    }
                    right={editPane}
                />
            )}
        </div>
    );
};

export default ActivityCategoryList;
